import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Archive, FileText, Upload, Search, FolderOpen, Folder, FolderPlus, Eye, Download, ArrowLeft, Pencil, Files, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { AuditLogButton } from '@/components/AuditLogButton';
import { toast } from 'sonner';
import { getFileUrl, isPdf, isImage } from '@/utils/fileUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CATEGORIES = ['generale', 'contratti', 'sicurezza', 'certificazioni', 'fatture', 'ddt', 'permessi', 'altro'];
const CATEGORY_LABELS = { generale: 'Generale', contratti: 'Contratti', sicurezza: 'Sicurezza', certificazioni: 'Certificazioni', fatture: 'Fatture', ddt: 'DDT', permessi: 'Permessi', altro: 'Altro' };
const CATEGORY_COLORS = { generale: 'bg-slate-100 text-slate-700', contratti: 'bg-blue-100 text-blue-700', sicurezza: 'bg-amber-100 text-amber-700', certificazioni: 'bg-emerald-100 text-emerald-700', fatture: 'bg-purple-100 text-purple-700', ddt: 'bg-orange-100 text-orange-700', permessi: 'bg-cyan-100 text-cyan-700', altro: 'bg-slate-100 text-slate-600' };

export default function ArchivioPage() {
  const [allFolders, setAllFolders] = useState([]); // flat list
  const [rootStats, setRootStats] = useState({ subfolder_count: 0, document_count: 0 });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [searchQuery, setSearchQuery] = useState('');

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', category: 'generale', tags: '' });
  // iter89: modalità upload — 'single' (default) o 'bulk' (multi-file, stessa categoria)
  const [uploadMode, setUploadMode] = useState('single');
  const [bulkFiles, setBulkFiles] = useState([]); // [{file, title, status, error}]
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  // Folder dialogs
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/archive/folders`, { withCredentials: true });
      setAllFolders(res.data?.folders || []);
      setRootStats(res.data?.root || { subfolder_count: 0, document_count: 0 });
    } catch (err) {
      console.error('Errore caricamento cartelle archivio:', err);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = currentFolderId ? { folder_id: currentFolderId } : { folder_id: 'root' };
      const res = await axios.get(`${API}/archive`, { params, withCredentials: true });
      setDocuments(res.data || []);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [currentFolderId]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Build current folder breadcrumb / parent chain
  const folderById = Object.fromEntries(allFolders.map(f => [f.id, f]));
  const currentFolder = currentFolderId ? folderById[currentFolderId] : null;

  // Subfolders of current folder
  const subfolders = allFolders.filter(f => (f.parent_id || null) === currentFolderId);

  // Search across all folders/docs (when query present)
  const isSearching = !!searchQuery.trim();
  const searchTerm = searchQuery.trim().toLowerCase();
  const matchedFolders = isSearching
    ? allFolders.filter(f => f.name.toLowerCase().includes(searchTerm) || (f.path || '').toLowerCase().includes(searchTerm))
    : [];

  // For local doc filtering when searching
  const [allDocs, setAllDocs] = useState([]);
  useEffect(() => {
    if (!isSearching) { setAllDocs([]); return; }
    let cancelled = false;
    axios.get(`${API}/archive`, { withCredentials: true }).then(res => {
      if (!cancelled) setAllDocs(res.data || []);
    }).catch((err) => {
      console.error('Errore ricerca archivio:', err);
    });
    return () => { cancelled = true; };
  }, [isSearching, searchTerm]);

  const matchedDocs = isSearching
    ? allDocs.filter(d => (d.title || '').toLowerCase().includes(searchTerm) || (d.description || '').toLowerCase().includes(searchTerm) || (d.tags || []).some(t => t.toLowerCase().includes(searchTerm)))
    : documents;

  // ---- Actions ----
  const goBack = () => {
    if (!currentFolderId) return;
    const parent = currentFolder?.parent_id || null;
    setCurrentFolderId(parent);
  };

  const enterFolder = (folder) => setCurrentFolderId(folder.id);

  const createFolder = async (e) => {
    e?.preventDefault?.();
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/archive/folders`, { name, parent_id: currentFolderId }, { withCredentials: true });
      toast.success('Cartella creata');
      setNewFolderOpen(false);
      setNewFolderName('');
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione cartella');
    }
  };

  const renameFolder = async (e) => {
    e?.preventDefault?.();
    const name = renameValue.trim();
    if (!name || !renameTarget) return;
    try {
      await axios.put(`${API}/archive/folders/${renameTarget.id}`, { name }, { withCredentials: true });
      toast.success('Cartella rinominata');
      setRenameOpen(false);
      setRenameTarget(null);
      setRenameValue('');
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const deleteFolder = async (folder) => {
    const isEmpty = (folder.subfolder_count || 0) === 0 && (folder.document_count || 0) === 0;
    const msg = isEmpty
      ? `Eliminare la cartella "${folder.name}"?`
      : `La cartella "${folder.name}" contiene ${folder.subfolder_count} sottocartelle e ${folder.document_count} documenti.\nEliminare TUTTO?`;
    if (!window.confirm(msg)) return;
    try {
      const params = isEmpty ? {} : { force: true };
      await axios.delete(`${API}/archive/folders/${folder.id}`, { params, withCredentials: true });
      toast.success('Cartella eliminata');
      fetchFolders();
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Seleziona un file'); return; }
    if (!formData.title?.trim()) { toast.error('Inserisci un titolo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const filePath = uploadRes.data?.path;
      if (!filePath) throw new Error('Upload fallito');
      await axios.post(`${API}/archive`, {
        ...formData,
        folder: '',
        folder_id: currentFolderId,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        file_url: `/api/files/${filePath}`,
        file_type: file.type || file.name.split('.').pop()
      }, { withCredentials: true });
      toast.success('Documento archiviato');
      setUploadOpen(false);
      setFile(null);
      setFormData({ title: '', description: '', category: 'generale', tags: '' });
      fetchDocuments();
      fetchFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Errore nell\'upload');
    }
    finally { setUploading(false); }
  };

  // iter89: gestione selezione file multipla per modalità bulk
  const onBulkFilesSelected = (fileList) => {
    const arr = Array.from(fileList || []).map((f) => ({
      file: f,
      // Titolo predefinito = basename senza estensione, editabile
      title: (f.name || '').replace(/\.[^.]+$/, '') || f.name || 'documento',
      status: 'pending', // pending | uploading | done | error
      error: '',
    }));
    setBulkFiles(arr);
  };

  const updateBulkTitle = (idx, title) => {
    setBulkFiles((prev) => prev.map((it, i) => (i === idx ? { ...it, title } : it)));
  };

  const removeBulkFile = (idx) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (bulkFiles.length === 0) { toast.error('Seleziona almeno un file'); return; }
    const missingTitle = bulkFiles.find((f) => !f.title?.trim());
    if (missingTitle) { toast.error('Ogni file deve avere un titolo'); return; }
    setUploading(true);
    setBulkProgress({ done: 0, total: bulkFiles.length });
    const commonTags = formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [];
    let ok = 0;
    let failed = 0;
    // Sequenziale per non sovraccaricare + progresso visibile
    for (let i = 0; i < bulkFiles.length; i++) {
      const item = bulkFiles[i];
      setBulkFiles((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: 'uploading' } : it)));
      try {
        const fd = new FormData();
        fd.append('file', item.file);
        const uploadRes = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        const filePath = uploadRes.data?.path;
        if (!filePath) throw new Error('Upload fallito');
        await axios.post(`${API}/archive`, {
          title: item.title.trim(),
          description: formData.description || '',
          category: formData.category,
          folder: '',
          folder_id: currentFolderId,
          tags: commonTags,
          file_url: `/api/files/${filePath}`,
          file_type: item.file.type || item.file.name.split('.').pop(),
        }, { withCredentials: true });
        setBulkFiles((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: 'done' } : it)));
        ok += 1;
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || 'Errore';
        setBulkFiles((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: 'error', error: msg } : it)));
        failed += 1;
      }
      setBulkProgress({ done: i + 1, total: bulkFiles.length });
    }
    setUploading(false);
    if (failed === 0) {
      toast.success(`${ok} document${ok === 1 ? 'o' : 'i'} archiviat${ok === 1 ? 'o' : 'i'}`);
      // Reset e chiusura dopo un breve delay per far vedere i checkmarks
      setTimeout(() => {
        setUploadOpen(false);
        setBulkFiles([]);
        setFormData({ title: '', description: '', category: 'generale', tags: '' });
        setBulkProgress({ done: 0, total: 0 });
      }, 800);
    } else {
      toast.error(`${failed} errore/i su ${bulkFiles.length} — controlla la lista`);
    }
    fetchDocuments();
    fetchFolders();
  };

  const resetUploadDialog = () => {
    setUploadOpen(false);
    setFile(null);
    setBulkFiles([]);
    setBulkProgress({ done: 0, total: 0 });
    setFormData({ title: '', description: '', category: 'generale', tags: '' });
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.title}"?`)) return;
    try {
      await axios.delete(`${API}/archive/${doc.id}`, { withCredentials: true });
      toast.success('Documento eliminato');
      fetchDocuments();
      fetchFolders();
    } catch { toast.error('Errore'); }
  };

  const openPreview = (doc) => {
    setPreviewDoc(doc);
    setPreviewOpen(true);
  };

  if (loading && !documents.length && !allFolders.length) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="archivio-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {currentFolderId && (
            <Button variant="outline" size="sm" onClick={goBack} data-testid="archive-back-btn" className="shrink-0">
              <ArrowLeft className="h-4 w-4 mr-1" />Indietro
            </Button>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 truncate flex items-center gap-2">
              {currentFolder ? <><FolderOpen className="h-5 w-5 text-amber-500 shrink-0" />{currentFolder.name}</> : <><Archive className="h-5 w-5 shrink-0" />Archivio</>}
            </h1>
            {currentFolder?.path && (
              <p className="text-xs text-slate-500 truncate">{currentFolder.path}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setNewFolderOpen(true); setNewFolderName(''); }} data-testid="add-folder-btn">
            <FolderPlus className="h-4 w-4 mr-1.5" />Nuova cartella
          </Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { setUploadMode('single'); setUploadOpen(true); }} data-testid="add-archive-btn">
            <Plus className="h-4 w-4 mr-1.5" />Carica documento
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setUploadMode('bulk'); setUploadOpen(true); }} data-testid="add-archive-bulk-btn">
            <Files className="h-4 w-4 mr-1.5" />Importazione in massa
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Cerca in tutto l'archivio..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="archive-search"
        />
      </div>

      {isSearching ? (
        // Search results: show matching folders + docs across all folders
        <div className="space-y-4">
          {matchedFolders.length > 0 && (
            <div>
              <p className="text-xs uppercase text-slate-500 font-medium mb-2">Cartelle ({matchedFolders.length})</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {matchedFolders.map(f => <FolderCard key={f.id} folder={f} onOpen={() => { setCurrentFolderId(f.id); setSearchQuery(''); }} onRename={() => { setRenameTarget(f); setRenameValue(f.name); setRenameOpen(true); }} onDelete={() => deleteFolder(f)} />)}
              </div>
            </div>
          )}
          {matchedDocs.length > 0 && (
            <div>
              <p className="text-xs uppercase text-slate-500 font-medium mb-2">Documenti ({matchedDocs.length})</p>
              <DocList docs={matchedDocs} folderById={folderById} onPreview={openPreview} onDelete={deleteDoc} showFolder />
            </div>
          )}
          {matchedFolders.length === 0 && matchedDocs.length === 0 && (
            <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-500">Nessun risultato per &quot;{searchQuery}&quot;</CardContent></Card>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Subfolders */}
          {subfolders.length > 0 && (
            <div>
              <p className="text-xs uppercase text-slate-500 font-medium mb-2">Cartelle ({subfolders.length})</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {subfolders.map(f => (
                  <FolderCard key={f.id} folder={f} onOpen={() => enterFolder(f)} onRename={() => { setRenameTarget(f); setRenameValue(f.name); setRenameOpen(true); }} onDelete={() => deleteFolder(f)} />
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <div>
              <p className="text-xs uppercase text-slate-500 font-medium mb-2">Documenti ({documents.length})</p>
              <DocList docs={documents} folderById={folderById} onPreview={openPreview} onDelete={deleteDoc} />
            </div>
          )}

          {subfolders.length === 0 && documents.length === 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center">
                <Archive className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 mb-1">Cartella vuota</p>
                <p className="text-xs text-slate-400">Crea una sottocartella o carica un documento</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Nuova cartella</DialogTitle></DialogHeader>
          <form onSubmit={createFolder} className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Nome</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={currentFolder ? `Sottocartella di ${currentFolder.name}` : 'Es. 2024, Fatture, Sicurezza...'}
                autoFocus
                data-testid="new-folder-input"
              />
              {currentFolder && (
                <p className="text-xs text-slate-400 mt-1">Verrà creata in: {currentFolder.path}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setNewFolderOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white" data-testid="confirm-new-folder">Crea</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Rinomina cartella</DialogTitle></DialogHeader>
          <form onSubmit={renameFolder} className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Nuovo nome</Label>
              <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { if (!v) resetUploadDialog(); else setUploadOpen(true); }}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="archive-upload-dialog">
          <DialogHeader>
            <DialogTitle>Carica document{uploadMode === 'bulk' ? 'i' : 'o'}{currentFolder ? ` in "${currentFolder.name}"` : ''}</DialogTitle>
          </DialogHeader>

          {/* iter89: toggle Singolo / In massa */}
          <div className="mt-2 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg" data-testid="archive-upload-mode-toggle">
            <button
              type="button"
              onClick={() => { setUploadMode('single'); setBulkFiles([]); }}
              className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${uploadMode === 'single' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="archive-upload-mode-single"
              disabled={uploading}
            >
              <FileText className="h-4 w-4" />Singolo
            </button>
            <button
              type="button"
              onClick={() => { setUploadMode('bulk'); setFile(null); }}
              className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${uploadMode === 'bulk' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="archive-upload-mode-bulk"
              disabled={uploading}
            >
              <Files className="h-4 w-4" />In massa
            </button>
          </div>

          {uploadMode === 'single' ? (
            <form onSubmit={handleUpload} className="space-y-3 mt-4">
              <div className="space-y-1.5">
                <Label>Titolo *</Label>
                <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required data-testid="archive-title" />
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione</Label>
                <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
              </div>
              <div className="space-y-1.5">
                <Label>Categoria (tipo di file) *</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="archive-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tag (separati da virgola)</Label>
                <Input value={formData.tags} onChange={e => setFormData({...formData, tags: e.target.value})} placeholder="es. 2024, scadenza, importante" />
              </div>
              <div className="space-y-1.5">
                <Label>File *</Label>
                <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} required data-testid="archive-file" />
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-slate-900 text-white" data-testid="archive-submit">
                <Upload className="h-4 w-4 mr-2" />{uploading ? 'Caricamento...' : 'Carica'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleBulkUpload} className="space-y-3 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Categoria (applicata a tutti) *</Label>
                  <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})} disabled={uploading}>
                    <SelectTrigger data-testid="archive-bulk-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tag comuni (opzionale)</Label>
                  <Input value={formData.tags} onChange={e => setFormData({...formData, tags: e.target.value})} placeholder="es. 2024, scadenza" disabled={uploading} data-testid="archive-bulk-tags" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione comune (opzionale)</Label>
                <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} disabled={uploading} />
              </div>
              <div className="space-y-1.5">
                <Label>Seleziona più file *</Label>
                <Input
                  type="file"
                  multiple
                  onChange={e => onBulkFilesSelected(e.target.files)}
                  disabled={uploading}
                  data-testid="archive-bulk-files-input"
                />
                <p className="text-xs text-slate-500">Puoi selezionare più file insieme (Ctrl / Cmd + click). Ogni file diventerà un documento separato, con la stessa categoria.</p>
              </div>

              {bulkFiles.length > 0 && (
                <div className="border rounded-lg divide-y max-h-[280px] overflow-y-auto" data-testid="archive-bulk-list">
                  <div className="px-3 py-2 bg-slate-50 flex items-center justify-between text-xs text-slate-600">
                    <span>{bulkFiles.length} file selezionati</span>
                    {uploading && bulkProgress.total > 0 && (
                      <span data-testid="archive-bulk-progress">Caricamento: {bulkProgress.done}/{bulkProgress.total}</span>
                    )}
                  </div>
                  {bulkFiles.map((it, idx) => (
                    <div key={`${it.file.name}-${idx}`} className="p-2.5 flex items-center gap-2" data-testid={`archive-bulk-row-${idx}`}>
                      <div className="w-6 flex-shrink-0 flex items-center justify-center">
                        {it.status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                        {it.status === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
                        {it.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />}
                        {it.status === 'pending' && <FileText className="h-4 w-4 text-slate-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <Input
                          value={it.title}
                          onChange={(e) => updateBulkTitle(idx, e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Titolo documento"
                          disabled={uploading}
                          data-testid={`archive-bulk-title-${idx}`}
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                          {it.file.name} · {(it.file.size / 1024).toFixed(1)} KB
                          {it.status === 'error' && it.error && <span className="text-red-600"> · {it.error}</span>}
                        </p>
                      </div>
                      {!uploading && it.status !== 'done' && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-400 hover:text-red-600"
                          onClick={() => removeBulkFile(idx)}
                          data-testid={`archive-bulk-remove-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="submit"
                disabled={uploading || bulkFiles.length === 0}
                className="w-full bg-slate-900 text-white"
                data-testid="archive-bulk-submit"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading
                  ? `Caricamento ${bulkProgress.done}/${bulkProgress.total}...`
                  : (bulkFiles.length > 0 ? `Carica ${bulkFiles.length} file` : 'Seleziona i file da caricare')}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] h-[90vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-4 py-3 border-b shrink-0"><DialogTitle className="truncate">{previewDoc?.title}</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewDoc?.file_url && (isImage(previewDoc.file_url) ? (
              <img src={getFileUrl(previewDoc.file_url)} alt={previewDoc.title} className="max-w-full max-h-full object-contain mx-auto" />
            ) : isPdf(previewDoc.file_url) ? (
              <iframe src={getFileUrl(previewDoc.file_url)} className="w-full h-full border-0" title="Anteprima" />
            ) : (
              <iframe src={getFileUrl(previewDoc.file_url)} className="w-full h-full border-0" title="Anteprima" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FolderCard({ folder, onOpen, onRename, onDelete }) {
  const itemCount = (folder.subfolder_count || 0) + (folder.document_count || 0);
  return (
    <Card
      className="group border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer relative"
      onClick={onOpen}
      data-testid={`folder-card-${folder.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Folder className="h-10 w-10 text-amber-400 fill-amber-100 mb-2" />
            <p className="font-medium text-slate-900 truncate text-sm">{folder.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {itemCount === 0 ? 'Vuota' : (
                <>
                  {folder.subfolder_count > 0 && <span>{folder.subfolder_count} cartell{folder.subfolder_count === 1 ? 'a' : 'e'}</span>}
                  {folder.subfolder_count > 0 && folder.document_count > 0 && <span> · </span>}
                  {folder.document_count > 0 && <span>{folder.document_count} file</span>}
                </>
              )}
            </p>
          </div>
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onRename(); }} data-testid={`rename-folder-${folder.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`delete-folder-${folder.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DocList({ docs, folderById, onPreview, onDelete, showFolder = false }) {
  return (
    <div className="space-y-2">
      {docs.map(doc => (
        <Card key={doc.id} className="border-slate-200 hover:border-slate-300 transition" data-testid={`doc-row-${doc.id}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-100 rounded shrink-0"><FileText className="h-5 w-5 text-slate-600" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm text-slate-900 truncate">{doc.title}</p>
                  {doc.category && <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[doc.category] || ''}`}>{CATEGORY_LABELS[doc.category] || doc.category}</Badge>}
                </div>
                {doc.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{doc.description}</p>}
                {showFolder && doc.folder_id && folderById[doc.folder_id] && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><FolderOpen className="h-3 w-3" />{folderById[doc.folder_id].path}</p>
                )}
                {doc.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {doc.tags.map(t => <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{t}</span>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <AuditLogButton fileId={doc.id} fileNome={doc.title} compact />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onPreview(doc)} data-testid={`preview-${doc.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                <a href={getFileUrl(doc.file_url)} target="_blank" rel="noopener noreferrer">
                  <Button size="icon" variant="ghost" className="h-8 w-8"><Download className="h-3.5 w-3.5" /></Button>
                </a>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onDelete(doc)} data-testid={`delete-doc-${doc.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
